import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Preview as PreviewIcon,
  Email as EmailIcon,
  Campaign as CampaignIcon,
  TrendingUp as TrendingIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { sanitizeHtml } from '../../../utils/safeHtml';

function CampaignTemplates({ onSelectTemplate }) {
  const theme = useTheme();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/campaigns/templates', {
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setDialogOpen(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setDialogOpen(true);
  };

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setPreviewDialogOpen(true);
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      // This would typically save to a templates collection
      // For now, we'll just close the dialog
      setDialogOpen(false);
      loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      // This would typically delete from a templates collection
      // For now, we'll just reload templates
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'marketing': return 'primary';
      case 'sales': return 'success';
      case 'newsletter': return 'info';
      case 'promotional': return 'warning';
      case 'educational': return 'secondary';
      case 'event': return 'error';
      default: return 'default';
    }
  };

  // Sample templates for demonstration
  const sampleTemplates = [
    {
      name: 'Property Listing Announcement',
      subject: 'New Property Available: {{property_address}}',
      category: 'marketing',
      usageCount: 15,
      variables: ['{{name}}', '{{property_address}}', '{{price}}', '{{agent_name}}'],
      description: 'Announce new property listings to your contact list',
    },
    {
      name: 'Open House Invitation',
      subject: 'Open House This Weekend: {{property_address}}',
      category: 'event',
      usageCount: 8,
      variables: ['{{name}}', '{{property_address}}', '{{date}}', '{{time}}', '{{agent_name}}'],
      description: 'Invite contacts to open house events',
    },
    {
      name: 'Market Update Newsletter',
      subject: 'Real Estate Market Update - {{month}} {{year}}',
      category: 'newsletter',
      usageCount: 12,
      variables: ['{{name}}', '{{month}}', '{{year}}', '{{market_stats}}'],
      description: 'Monthly market updates and insights',
    },
    {
      name: 'Price Reduction Alert',
      subject: 'Price Reduced: {{property_address}} - Now {{new_price}}',
      category: 'promotional',
      usageCount: 6,
      variables: ['{{name}}', '{{property_address}}', '{{old_price}}', '{{new_price}}'],
      description: 'Notify contacts about price reductions',
    },
    {
      name: 'Follow-up After Showing',
      subject: 'Thank you for viewing {{property_address}}',
      category: 'sales',
      usageCount: 20,
      variables: ['{{name}}', '{{property_address}}', '{{agent_name}}', '{{agent_phone}}'],
      description: 'Follow up with prospects after property showings',
    },
    {
      name: 'Holiday Greeting',
      subject: 'Happy {{holiday}} from {{agent_name}}',
      category: 'marketing',
      usageCount: 4,
      variables: ['{{name}}', '{{holiday}}', '{{agent_name}}'],
      description: 'Seasonal greetings to maintain relationships',
    },
  ];

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Email Templates
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Pre-built templates for your marketing campaigns
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTemplate}
        >
          Create Template
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {sampleTemplates.map((template, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Avatar sx={{
                      bgcolor: alpha(theme.palette[getCategoryColor(template.category)].main, 0.1),
                      color: theme.palette[getCategoryColor(template.category)].main,
                      width: 32,
                      height: 32,
                    }}
                    >
                      <EmailIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {template.name}
                      </Typography>
                      <Chip
                        label={template.category}
                        color={getCategoryColor(template.category)}
                        size="small"
                      />
                    </Box>
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {template.description}
                  </Typography>

                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Subject:
                    {' '}
                    {template.subject}
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Variables:
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {template.variables.map((variable) => (
                        <Chip
                          key={variable}
                          label={variable}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TrendingIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      Used
                      {' '}
                      {template.usageCount}
                      {' '}
                      times
                    </Typography>
                  </Stack>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between' }}>
                  <Button
                    size="small"
                    onClick={() => onSelectTemplate(template)}
                    startIcon={<CampaignIcon />}
                  >
                    Use Template
                  </Button>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Preview">
                      <IconButton
                        size="small"
                        onClick={() => handlePreviewTemplate(template)}
                      >
                        <PreviewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                      <IconButton size="small">
                        <DuplicateIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditTemplate(template)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTemplate(template._id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Template Form Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <EmailIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            {selectedTemplate ? 'Edit Template' : 'Create Template'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Stack spacing={3}>
            <TextField
              label="Template Name"
              value={selectedTemplate?.name || ''}
              fullWidth
              required
            />

            <TextField
              label="Subject Line"
              value={selectedTemplate?.subject || ''}
              fullWidth
              required
              helperText="This is what recipients will see in their inbox"
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedTemplate?.category || 'marketing'}
                label="Category"
              >
                <MenuItem value="marketing">Marketing</MenuItem>
                <MenuItem value="sales">Sales</MenuItem>
                <MenuItem value="newsletter">Newsletter</MenuItem>
                <MenuItem value="promotional">Promotional</MenuItem>
                <MenuItem value="educational">Educational</MenuItem>
                <MenuItem value="event">Event</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Description"
              value={selectedTemplate?.description || ''}
              fullWidth
              multiline
              rows={2}
            />

            <TextField
              label="Email Content"
              value={selectedTemplate?.htmlContent || ''}
              fullWidth
              multiline
              rows={8}
              placeholder="Enter your email content here. You can use variables like {{name}}, {{property_address}}, etc."
            />

            <TextField
              label="Plain Text Version"
              value={selectedTemplate?.textContent || ''}
              fullWidth
              multiline
              rows={4}
              helperText="Plain text version for email clients that don't support HTML"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSaveTemplate(selectedTemplate)}
          >
            {selectedTemplate ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'info.main',
          color: 'info.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <PreviewIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Template Preview
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 4 } }}>
          {previewTemplate && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Subject:
                  {' '}
                  {previewTemplate.subject}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Email Content:
                </Typography>
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    bgcolor: 'background.paper',
                    maxHeight: 400,
                    overflow: 'auto',
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewTemplate.htmlContent || '<p>No content available</p>') }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Available Variables:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {previewTemplate.variables?.map((variable) => (
                    <Chip
                      key={variable}
                      label={variable}
                      size="small"
                      variant="outlined"
                    />
                  )) || []}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setPreviewDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setPreviewDialogOpen(false);
              onSelectTemplate(previewTemplate);
            }}
          >
            Use This Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CampaignTemplates;
